import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';

import { useAPI } from 'hooks/api/useAPI';
import { updateQueriesData } from 'services/update_queries_data';

type UseDeleteTrackableListsParams = {
  trackable_list_ids: string[];
};

export function useDeleteTrackableLists({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<void, APIError, UseDeleteTrackableListsParams> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ trackable_list_ids }) => {
      await client.delete(`/apps/${applicationID}/trackables`, {
        params: { 'trackable_list_ids[]': trackable_list_ids },
      });
    },
    {
      ...options,
      onMutate: async (params) => {
        onMutate?.(params);

        return updateQueriesData({
          queryClient,
          listQueryKey: ['trackable-lists'],
          singleQueryKey: ['trackable-list'],
          ids: params.trackable_list_ids,
          updateData: null,
        });
      },
      onError: (error, params, context) => {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled: (...args) => {
        onSettled?.(...args);

        queryClient.invalidateQueries(['trackable-lists']);
        queryClient.invalidateQueries(['trackable-list']);
      },
    }
  );
}